import { useEffect, useRef } from "react";
import io from "socket.io-client";

const CONNECTION = process.env.REACT_APP_SERVER_URL || "/";

const getSocketConfig = () => {
  if (process.env.REACT_APP_SOCKET_PATH) {
    return {
      connection: CONNECTION,
      path: process.env.REACT_APP_SOCKET_PATH
    };
  }
  
  let baseConnection = CONNECTION;
  let socketPath = "/socket.io";
  
  if (CONNECTION.endsWith('/api')) {
    baseConnection = CONNECTION.slice(0, -4);
    socketPath = "/api/socket.io";
  } else if (CONNECTION === "/api") {
    baseConnection = "/";
    socketPath = "/api/socket.io";
  } else if (CONNECTION.includes('/api/')) {
    socketPath = "/api/socket.io";
  }
  
  return {
    connection: baseConnection,
    path: socketPath
  };
};

const socketConfig = getSocketConfig();
const CONNECTION_URL = socketConfig.connection;
const SOCKET_PATH = socketConfig.path;

const testApiConnectivity = async () => {
  const testUrl = CONNECTION_URL.endsWith('/') 
    ? `${CONNECTION_URL}api/test`
    : `${CONNECTION_URL}/api/test`;
  
  console.log(`[API TEST] Testing HTTP connectivity to: ${testUrl}`);
  
  try {
    const startTime = Date.now();
    const response = await fetch(testUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const endTime = Date.now();
    
    const contentType = response.headers.get('content-type');
    const responseText = await response.text();
    
    console.log(`[API TEST] Response Details:`, {
      status: response.status,
      statusText: response.statusText,
      contentType: contentType,
      responseTime: `${endTime - startTime}ms`,
      url: testUrl,
      responsePreview: responseText.substring(0, 200)
    });
    
    let data = null;
    if (contentType && contentType.includes('application/json')) {
      try {
        data = JSON.parse(responseText);
        console.log(`[API TEST] ✅ SUCCESS - JSON Response:`, data);
      } catch (parseError) {
        console.error(`[API TEST] ⚠️ JSON Parse Error:`, parseError);
        console.log(`[API TEST] Raw Response:`, responseText);
      }
    } else {
      console.log(`[API TEST] ⚠️ Non-JSON Response (${contentType}):`, responseText.substring(0, 500));
    }
    
    return { 
      success: response.ok && data !== null, 
      response, 
      data, 
      responseTime: endTime - startTime,
      contentType,
      rawResponse: responseText
    };
  } catch (error) {
    console.error(`[API TEST] ❌ FAILED:`, {
      error: error.message,
      errorType: error.name,
      url: testUrl,
      connectionUrl: CONNECTION_URL,
      socketPath: SOCKET_PATH,
      stack: error.stack
    });
    return { success: false, error };
  }
};

const logSocketEvent = (event, details = {}) => {
  const timestamp = new Date().toISOString();
  const logData = {
    timestamp,
    event,
    connectionUrl: CONNECTION_URL,
    socketPath: SOCKET_PATH,
    originalServerUrl: CONNECTION,
    ...details
  };
  
  console.log(`[SOCKET] ${event}`, logData);
};

export const useSocketConnection = () => {
  const socket = useRef();
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 10; // Increased from 5 to 10 for mobile

  useEffect(() => {
    logSocketEvent("INITIALIZING", { 
      connectionUrl: CONNECTION_URL,
      socketPath: SOCKET_PATH,
      originalServerUrl: CONNECTION,
      transports: ["websocket"]
    });

    testApiConnectivity();

    socket.current = io(CONNECTION_URL, {
      path: SOCKET_PATH,
      transports: ["websocket", "polling"], // Add polling fallback for mobile
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 10, // Increased from 5 to 10 for mobile
      timeout: 30000, // Increased from 20s to 30s for mobile
    });

    const currentSocket = socket.current;

    currentSocket.on("connect", () => {
      reconnectAttemptsRef.current = 0;
      logSocketEvent("CONNECTED", {
        socketId: currentSocket.id,
        transport: currentSocket.io.engine.transport.name,
      });
    });

    currentSocket.on("connect_error", (error) => {
      reconnectAttemptsRef.current += 1;
      
      const errorDetails = {
        message: error.message,
        type: error.type || "unknown",
        description: error.description || "Connection failed",
        reconnectAttempt: reconnectAttemptsRef.current,
        maxAttempts: maxReconnectAttempts,
        connectionUrl: CONNECTION,
      };

      if (error.message) {
        if (error.message.includes("xhr poll error")) {
          errorDetails.suggestion = "Server may be unreachable. Check if the server is running and the URL is correct.";
        } else if (error.message.includes("websocket error")) {
          errorDetails.suggestion = "WebSocket connection failed. Check network connectivity and firewall settings.";
        } else if (error.message.includes("timeout")) {
          errorDetails.suggestion = "Connection timeout. Server may be slow to respond or unreachable.";
        } else if (error.message.includes("CORS")) {
          errorDetails.suggestion = "CORS error. Check server CORS configuration.";
        }
      }

      logSocketEvent("CONNECT_ERROR", errorDetails);
      console.error(`[SOCKET ERROR] Failed to connect to ${CONNECTION_URL}${SOCKET_PATH}`, errorDetails);
      
      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        logSocketEvent("MAX_RECONNECT_ATTEMPTS_REACHED", {
          attempts: reconnectAttemptsRef.current,
          connectionUrl: CONNECTION_URL,
          socketPath: SOCKET_PATH,
        });
        console.error(`[SOCKET ERROR] Maximum reconnection attempts (${maxReconnectAttempts}) reached. Please refresh the page.`);
      }
    });

    currentSocket.on("disconnect", (reason) => {
      logSocketEvent("DISCONNECTED", {
        reason: reason || "unknown",
        socketId: currentSocket.id,
      });

      if (reason === "io server disconnect") {
        console.warn("[SOCKET] Server forcibly disconnected the client. You may need to reconnect.");
      } else if (reason === "io client disconnect") {
        console.info("[SOCKET] Client manually disconnected.");
      } else if (reason === "ping timeout") {
        console.warn("[SOCKET] Connection lost due to ping timeout. Attempting to reconnect...");
      } else if (reason === "transport close") {
        console.warn("[SOCKET] Connection closed. Attempting to reconnect...");
      } else if (reason === "transport error") {
        console.error("[SOCKET] Transport error occurred. Attempting to reconnect...");
      }
    });

    currentSocket.on("reconnect_attempt", (attemptNumber) => {
      logSocketEvent("RECONNECT_ATTEMPT", {
        attempt: attemptNumber,
        maxAttempts: maxReconnectAttempts,
      });
      console.log(`[SOCKET] Reconnection attempt ${attemptNumber}/${maxReconnectAttempts}...`);
    });

    currentSocket.on("server_error", (errorDetails) => {
      logSocketEvent("SERVER_ERROR", errorDetails);
      console.error("[SERVER ERROR]", errorDetails);
      
      const errorMessage = errorDetails.message || "A server error occurred. Please refresh the page.";
      alert(`🚨 Server Error\n\n${errorMessage}\n\nError ID: ${errorDetails.errorId || 'N/A'}\n\nPlease refresh the page if the issue persists.`);
      
      if (errorDetails.debug) {
        console.error("[SERVER ERROR DEBUG]", errorDetails.debug);
      }
    });

    currentSocket.on("reconnect", (attemptNumber) => {
      reconnectAttemptsRef.current = 0;
      logSocketEvent("RECONNECTED", {
        attempts: attemptNumber,
        socketId: currentSocket.id,
      });
      console.log(`[SOCKET] Successfully reconnected after ${attemptNumber} attempt(s).`);
    });

    currentSocket.on("reconnect_failed", () => {
      logSocketEvent("RECONNECT_FAILED", {
        attempts: reconnectAttemptsRef.current,
        maxAttempts: maxReconnectAttempts,
      });
      console.error(`[SOCKET ERROR] Failed to reconnect after ${maxReconnectAttempts} attempts. Please refresh the page.`);
    });

    currentSocket.on("connect_timeout", (timeout) => {
      logSocketEvent("CONNECT_TIMEOUT", {
        timeout,
        connectionUrl: CONNECTION_URL,
        socketPath: SOCKET_PATH,
      });
      console.error(`[SOCKET ERROR] Connection timeout after ${timeout}ms. Server may be unreachable.`);
    });

    currentSocket.on("error", (error) => {
      logSocketEvent("ERROR", {
        error: error.message || error,
        type: error.type || "unknown",
        connectionUrl: CONNECTION_URL,
        socketPath: SOCKET_PATH,
      });
      console.error("[SOCKET ERROR] General error:", error);
    });

    // Handle page visibility changes (mobile app switching)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page going to background - socket may be suspended
        console.log('[SOCKET] Page hidden - connection may suspend');
      } else {
        // Page returning to foreground
        console.log('[SOCKET] Page visible - checking connection');
        if (currentSocket && !currentSocket.connected) {
          console.log('[SOCKET] Reconnecting after page visibility change');
          currentSocket.connect();
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also handle pagehide/pageshow for iOS Safari
    const handlePageShow = () => {
      if (currentSocket && !currentSocket.connected) {
        console.log('[SOCKET] Page shown - reconnecting');
        currentSocket.connect();
      }
    };
    
    window.addEventListener('pageshow', handlePageShow);
    
    // Handle network online/offline events
    const handleOnline = () => {
      console.log('[SOCKET] Network online - reconnecting if needed');
      if (currentSocket && !currentSocket.connected) {
        currentSocket.connect();
      }
    };
    
    const handleOffline = () => {
      console.log('[SOCKET] Network offline');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      if (currentSocket) {
        logSocketEvent("CLEANUP", { socketId: currentSocket.id });
        currentSocket.removeAllListeners();
        currentSocket.disconnect();
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return socket;
};

